import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getIncident, getIncidentHistory, updateIncidentStatus, assignIncident, deleteIncident, getUsers, escalateIncident, getEscalations, updateIncident, getIncidentNotifications, getIncidentReview, createIncidentReview } from '../../shared/api/api';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { getUserIdentifier } from '../../shared/auth/session';
import { apiErrorMessage } from '../../shared/api/errorMessage';
import './IncidentDetailPage.css';

const STATUS_OPTIONS = ['NEW', 'IN_PROGRESS', 'PENDING_VERIFY', 'RESOLVED', 'CLOSED'];

// BRS US 6.3: "escalate to the next level on demand". The chain is a ladder
// climbed one rung at a time, by the SLA monitor automatically and by a person
// manually, and it only ever moves forward — so nobody can jump to the Executive
// on a whim, and nobody can quietly escalate backwards to make a problem look
// smaller. The escalate endpoint accepts no target role at all, which is why.
const ESCALATION_CHAIN = ['FIRST_RESPONDER', 'DEV_LEAD', 'DELIVERY_MANAGER', 'EXECUTIVE'];

const ROLE_LABELS = {
  FIRST_RESPONDER: 'First Responder',
  DEV_LEAD: 'Dev Lead',
  DELIVERY_MANAGER: 'Delivery Manager',
  EXECUTIVE: 'Executive',
};

/**
 * How far up the chain this incident has been escalated.
 *
 * Taken as the furthest role reached rather than the last entry in the list:
 * the API returns escalations newest-first, so array position is not "latest",
 * and reading it that way marked the wrong step as current on the timeline. The
 * chain only moves forward, so furthest and latest are the same thing — and this
 * way it does not matter which order they arrive in.
 *
 * An incident with no escalations sits at FIRST_RESPONDER, which is where the
 * first escalation is recorded as coming from.
 */
const currentEscalationIndex = (escalations) =>
  escalations.reduce((furthest, e) => Math.max(furthest, ESCALATION_CHAIN.indexOf(e.to_role)), 0);

function ReadOnlyField({ label, value, mono }) {
  return (
    <div className="id-inv-item">
      <span className="id-inv-label">{label}</span>
      <span className={`id-inv-value id-inv-value--filled${mono ? ' id-inv-value--mono' : ''}`}>
        {value ?? '—'}
      </span>
    </div>
  );
}

function IncidentDetailPage() {
  const { service, incidentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [incident, setIncident] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Status transition state
  const [editingStatus, setEditingStatus] = useState(false);
  const [showStatusNote, setShowStatusNote] = useState(false);
  const [pendingStatus, setPendingStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');

  // Assignee state
  const [editingAssignee, setEditingAssignee] = useState(false);
  const [users, setUsers] = useState([]);

  // Escalation state
  const [escalations, setEscalations] = useState([]);
  const [showEscalateForm, setShowEscalateForm] = useState(false);
  const [escalateToRole, setEscalateToRole] = useState('');
  const [escalateReason, setEscalateReason] = useState('');
  const [escalating, setEscalating] = useState(false);
  // Dialog-local, like resolutionError below: a page-level message renders in
  // the body behind the overlay, where the person who caused it cannot see it.
  const [escalateError, setEscalateError] = useState('');

  // Notifications panel state
  const [notifications, setNotifications] = useState([]);
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');

  // Post-incident review state
  const [review, setReview] = useState(null);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRcaSummary, setReviewRcaSummary] = useState('');
  const [reviewContributingFactors, setReviewContributingFactors] = useState('');
  const [reviewActionItems, setReviewActionItems] = useState('');
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState('');       // save failed — shown in the dialog
  const [reviewLoadError, setReviewLoadError] = useState(''); // fetch failed — shown in the tab

  // Resolution form state
  const [showResolutionForm, setShowResolutionForm] = useState(false);
  const [resolutionType, setResolutionType] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [savingResolution, setSavingResolution] = useState(false);
  // TSB-4214: a save failure must be readable from inside the dialog. The
  // page-level `error` banner renders in the page body, but the dialog is a
  // position: fixed overlay at z-index 1000, so it was painted behind the
  // dialog obscuring it — Save appeared to do nothing.
  const [resolutionError, setResolutionError] = useState('');

  // Active bottom tab
  const [activeTab, setActiveTab] = useState('activity');

  const statusRef = useRef(null);
  const assigneeRef = useRef(null);

  useEffect(() => {
    fetchIncident();
    fetchHistory();
    fetchUsers();
    fetchEscalations();
    fetchNotifications();
    fetchReview();
  }, [service, incidentId]);

  const fetchIncident = async () => {
    setLoading(true);
    try {
      const response = await getIncident(service, incidentId);
      setIncident(response.data);
    } catch {
      setError('Failed to load incident details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await getIncidentHistory(service, incidentId);
      const data = response.data;
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      // silently fail
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      const data = response.data;
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      // silently fail
    }
  };

  const fetchEscalations = async () => {
    try {
      const response = await getEscalations(service, incidentId);
      const data = response.data;
      setEscalations(Array.isArray(data) ? data : []);
    } catch {
      // silently fail
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await getIncidentNotifications(service, incidentId);
      const data = response.data;
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      // silently fail
    }
  };

  const fetchReview = async () => {
    try {
      const response = await getIncidentReview(service, incidentId);
      // 204 means the incident has no review yet, and carries no body.
      setReview(response.status === 200 ? response.data : null);
      setReviewLoadError('');
    } catch (err) {
      // 404 says the same thing as 204 here: nothing recorded yet, not a fault.
      if (err.response?.status === 404) {
        setReview(null);
        setReviewLoadError('');
        return;
      }
      // Anything else is a genuine failure. Swallowing it made a broken lookup
      // indistinguishable from "no review submitted yet" — which is how a
      // failure came to be reported as an empty section.
      setReviewLoadError('Could not load the post-incident review. It may exist but could not be read.');
    }
  };

  const handleSaveReview = async () => {
    // The API requires rca_summary AND reviewed_by. The dialog already enforces
    // the first by disabling its button; this covers the second, which was
    // being sent unchecked and rejected server-side with no visible reason.
    const reviewedBy = getUserIdentifier();
    if (!reviewedBy) {
      setReviewError(
        'Cannot record who is submitting this review. Your session has no email address against it — sign out and back in, and raise this if it persists.'
      );
      return;
    }

    setSavingReview(true);
    setReviewError('');
    try {
      const response = await createIncidentReview(service, incidentId, {
        rca_summary: reviewRcaSummary,
        contributing_factors: reviewContributingFactors || undefined,
        action_items: reviewActionItems || undefined,
        reviewed_by: reviewedBy,
      });
      setReview(response.data);
      setReviewLoadError('');
      setShowReviewForm(false);
      fetchHistory();
    } catch (err) {
      // Keep what the API said, field by field. Its top-level `message` is
      // "Invalid request format" for every validation failure in the system, so
      // the per-field `details` are the only part worth reading.
      setReviewError(apiErrorMessage(err, 'Failed to save post-incident review.'));
    } finally {
      setSavingReview(false);
    }
  };

  // Both fields are optional — UpdateIncidentRequest requires nothing, and no
  // business rule makes either mandatory, so a type without notes and notes
  // without a type are both legitimate. What is not legitimate is saving
  // neither: that sent an empty body, the API accepted it because there was
  // nothing to reject, and the dialog closed reporting success over a request
  // that changed nothing. The card still read "Not set" afterwards.
  const hasResolutionToSave = Boolean(resolutionType || resolutionNotes.trim());

  const handleSaveResolution = async () => {
    if (!hasResolutionToSave) return;

    setSavingResolution(true);
    setResolutionError('');
    try {
      const response = await updateIncident(service, incidentId, {
        resolution_type: resolutionType || undefined,
        // Trimmed: whitespace is not a note, and sending it would overwrite a
        // real one already on the incident.
        resolution_notes: resolutionNotes.trim() || undefined,
      });
      setIncident(response.data);
      setShowResolutionForm(false);
    } catch (err) {
      setResolutionError(apiErrorMessage(err, 'Failed to save resolution details.'));
    } finally {
      setSavingResolution(false);
    }
  };

  const escalationIndex = currentEscalationIndex(escalations);
  const nextEscalationRole = ESCALATION_CHAIN[escalationIndex + 1] || null;

  const handleEscalate = async () => {
    // ManualEscalateRequest requires reason and triggered_by. Both were being
    // sent unvalidated — an empty reason was dropped by the `|| undefined`
    // below, so the request went out already guaranteed to be rejected.
    if (!escalateToRole || !escalateReason.trim()) return;

    const triggeredBy = getUserIdentifier();
    if (!triggeredBy) {
      setEscalateError(
        'Cannot record who is escalating. Your session has no email address against it — sign out and back in, and raise this if it persists.'
      );
      return;
    }

    setEscalating(true);
    setEscalateError('');
    try {
      await escalateIncident(service, incidentId, {
        to_role: escalateToRole,
        reason: escalateReason.trim(),
        triggered_by: triggeredBy,
      });
      setShowEscalateForm(false);
      setEscalateToRole('');
      setEscalateReason('');
      fetchEscalations();
      fetchHistory();
    } catch (err) {
      setEscalateError(apiErrorMessage(err, 'Failed to escalate incident.'));
    } finally {
      setEscalating(false);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setEditingStatus(false);
      }
      if (assigneeRef.current && !assigneeRef.current.contains(e.target)) {
        setEditingAssignee(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /**
   * Why a resolution type is demanded here and not in the Resolution Details
   * form: it is the only field on that form anyone can report on — free text
   * cannot be totalled, an enum can — so leaving it optional means the
   * resolution-type figures under-count by an unknown amount. But it can only be
   * answered truthfully once the incident is actually resolved. Demanding it in
   * the form, which can be opened on an incident still marked NEW, would push
   * people to pick something arbitrary to save their notes, and an invented
   * CLEAN_UP is worse than a blank: blank is visible, a guess is not.
   *
   * So the requirement lives at the transition, which is the moment the answer
   * exists. Partial progress in the form stays possible at any time.
   *
   * OTHER carries no information by itself, so it brings the notes with it.
   *
   * The browser cannot be the enforcement point — the API accepts these fields
   * from anyone with or without them — so this needs the same rule server-side
   * to be true of the data rather than merely true of this form.
   */
  const resolutionBlocker = (newStatus) => {
    if (newStatus !== 'RESOLVED') return '';
    if (!incident.resolution_type) {
      return 'Set a resolution type before resolving this incident — use Add Resolution on the Investigation card.';
    }
    if (incident.resolution_type === 'OTHER' && !incident.resolution_notes?.trim()) {
      return 'A resolution type of Other needs resolution notes explaining what was done.';
    }
    return '';
  };

  const handleStatusSelect = (newStatus) => {
    setEditingStatus(false);
    if (newStatus === incident.status) return;

    const blocker = resolutionBlocker(newStatus);
    if (blocker) {
      setError(blocker);
      return;
    }

    setError('');
    setPendingStatus(newStatus);
    setStatusNote('');
    setShowStatusNote(true);
  };

  const handleStatusConfirm = async () => {
    setShowStatusNote(false);
    setSaving(true);
    setError('');
    try {
      const response = await updateIncidentStatus(service, incidentId, {
        status: pendingStatus,
        changed_by: getUserIdentifier(),
        note: statusNote || undefined,
      });
      setIncident(response.data);
      fetchHistory();
    } catch (err) {
      const errData = err.response?.data;
      if (err.response?.status === 400 && errData?.details?.allowed_transitions) {
        setError(`Cannot transition from ${errData.details.current_status} to ${pendingStatus}. Allowed: ${errData.details.allowed_transitions.join(', ')}`);
      } else {
        setError(apiErrorMessage(err, 'Failed to update status.'));
      }
    } finally {
      setSaving(false);
      setPendingStatus('');
      setStatusNote('');
    }
  };

  const handleStatusCancel = () => {
    setShowStatusNote(false);
    setPendingStatus('');
    setStatusNote('');
  };

  const handleAssigneeSelect = async (username) => {
    setEditingAssignee(false);
    if (username !== (incident.assignee || '')) {
      setSaving(true);
      setError('');
      try {
        const response = await assignIncident(service, incidentId, {
          assignee: username || null,
          assigned_by: getUserIdentifier(),
        });
        setIncident(response.data);
        fetchHistory();
      } catch (err) {
        setError(apiErrorMessage(err, 'Failed to update assignee.'));
      } finally {
        setSaving(false);
      }
    }
  };

  const handleCancelIncident = async () => {
    setShowCancelConfirm(false);
    setDeleting(true);
    setError('');
    try {
      await deleteIncident(service, incidentId);
      navigate('/incidents', { state: { flash: 'Incident cancelled.' } });
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to cancel incident.'));
      setDeleting(false);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity?.toUpperCase()) {
      case 'CRITICAL': return 'critical';
      case 'HIGH': return 'high';
      case 'MEDIUM': return 'medium';
      case 'LOW': return 'low';
      default: return 'info';
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'NEW': return 'new';
      case 'IN_PROGRESS': return 'in-progress';
      case 'PENDING_VERIFY': return 'pending-verify';
      case 'RESOLVED': return 'resolved';
      case 'CLOSED': return 'closed';
      default: return 'unknown';
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleString();
  };

  const formatRelativeTime = (ts) => {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const isSlaBreach = incident && incident.sla_deadline && !['RESOLVED', 'CLOSED'].includes(incident.status) && new Date(incident.sla_deadline) < new Date();

  const failedNotifCount = notifications.filter(n => !n.success).length;

  if (!loading && !incident) {
    return (
      <div className="page-container">
        <div className="alert alert-error">{error || 'Incident not found.'}</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {loading && <LoadingSpinner message="Loading incident details..." />}

      {/* Notifications backdrop + panel */}
      <div className={`id-notif-backdrop${notifPanelOpen ? ' id-notif-backdrop--open' : ''}`} onClick={() => setNotifPanelOpen(false)} />

      {/* Notifications slide-in panel */}
      <div className={`id-notif-panel${notifPanelOpen ? ' id-notif-panel--open' : ''}`}>
        <div className="id-notif-panel-header">
          <span className="id-notif-panel-title">Notifications</span>
          <button className="id-notif-panel-close" onClick={() => setNotifPanelOpen(false)}>&#x2715;</button>
        </div>
        <div className="id-notif-panel-body">
          {notifications.length === 0 ? (
            <p className="id-no-history">No notifications sent yet.</p>
          ) : (
            notifications.map((n) => (
              <div key={n.notification_id} className="id-np-item">
                <div className="id-np-item-top">
                  <span className={`id-notification-type id-notification-type--${n.success ? 'success' : 'failed'}`}>
                    {n.type?.replace(/_/g, ' ')}
                  </span>
                  <span className="id-history-time">{formatRelativeTime(n.sent_at)}</span>
                </div>
                <p className="id-notification-subject">{n.subject}</p>
                {!n.success && n.error_message && (
                  <p className="id-notification-error">{n.error_message}</p>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {incident && (
        <div className="id-page-content">

          {/* Top bar */}
          <div className="id-page-top">
            <button
              className="btn-back"
              onClick={() => navigate('/incidents', { state: location.state })}
            >
              {'←'} Back to Incidents
            </button>
            <div className="id-page-actions">
              <button className="btn id-cancel-btn id-cancel-btn--inline" onClick={() => setShowCancelConfirm(true)} disabled={deleting}>
                {deleting ? 'Cancelling...' : 'Cancel Incident'}
              </button>
              {incident.alert_id && (
                <button className="btn btn-secondary id-escalate-btn" onClick={() => navigate(`/alerts/${incident.alert_id}`)}>
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  View Alert
                </button>
              )}
              <button
                className={`btn btn-secondary id-escalate-btn id-notif-btn${notifPanelOpen ? ' id-notif-btn--active' : ''}`}
                onClick={() => setNotifPanelOpen(!notifPanelOpen)}
                title="Notifications"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                Notifications
                {failedNotifCount > 0 && (
                  <span className="id-notif-badge">{failedNotifCount}</span>
                )}
              </button>
            </div>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {saving && <div className="id-saving-indicator">Saving...</div>}

          {/* SLA breach banner */}
          {isSlaBreach && (
            <div className="id-sla-breach-banner">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>SLA breached — this incident passed its deadline on {new Date(incident.sla_deadline).toLocaleString()}</span>
            </div>
          )}

          {/* Two-column layout */}
          <div className="id-layout">

            {/* ── MAIN COLUMN ── */}
            <div className="id-main">

              {/* Header card */}
              <div className="card id-card">
                <div className="id-header-top">
                  <span className="id-ref-number">{incident.reference_number}</span>
                  <span className={`incidents-severity incidents-severity--${getSeverityClass(incident.severity)}`}>
                    {incident.severity}
                  </span>
                  {incident.priority && (
                    <span className="id-priority-badge">{incident.priority}</span>
                  )}
                </div>
                <h1 className="id-title">{incident.title || incident.alarm_name}</h1>
                <p className="id-description">{incident.description || incident.alarm_description}</p>

                {/* Key facts bar */}
                <div className="id-key-facts">
                  <div className="id-key-fact">
                    <span className="id-key-label">Service</span>
                    <span className="id-key-value">{incident.service}</span>
                  </div>
                  <div className="id-key-fact">
                    <span className="id-key-label">Incident ID</span>
                    <span className="id-key-value id-key-mono">{incident.incident_id}</span>
                  </div>
                  <div className="id-key-fact">
                    <span className="id-key-label">Alert ID</span>
                    <span className="id-key-value id-key-mono">{incident.alert_id || '—'}</span>
                  </div>
                  <div className="id-key-fact">
                    <span className="id-key-label">Alarm Name</span>
                    <span className="id-key-value">{incident.alarm_name}</span>
                  </div>
                  <div className="id-key-fact">
                    <span className="id-key-label">Created</span>
                    <span className="id-key-value">{formatTimestamp(incident.created_at)}</span>
                  </div>
                  <div className="id-key-fact">
                    <span className="id-key-label">Resolved</span>
                    <span className={`id-key-value${!incident.resolved_at ? ' id-key-muted' : ''}`}>
                      {incident.resolved_at ? formatTimestamp(incident.resolved_at) : '—'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Investigation card */}
              <div className="card id-card">
                <div className="id-section-header">
                  <h2 className="section-title">Investigation</h2>
                  {!['RESOLVED', 'CLOSED'].includes(incident.status) && (
                    <button className="btn btn-secondary id-escalate-btn" onClick={() => { setResolutionError(''); setResolutionType(incident.resolution_type || ''); setResolutionNotes(incident.resolution_notes || ''); setShowResolutionForm(true); }}>
                      + Add Resolution
                    </button>
                  )}
                </div>
                <div className="id-inv-grid">
                  <div className="id-inv-field id-inv-field--full">
                    <span className="id-inv-label">Root Cause</span>
                    {incident.root_cause
                      ? <span className="id-inv-value id-inv-value--filled">{incident.root_cause}</span>
                      : <span className="id-inv-value id-inv-value--empty">Not identified yet</span>
                    }
                  </div>
                  <div className="id-inv-field id-inv-field--full">
                    <span className="id-inv-label">Client Blast Radius</span>
                    {incident.client_blast_radius
                      ? <span className="id-inv-value id-inv-value--filled">{incident.client_blast_radius}</span>
                      : <span className="id-inv-value id-inv-value--empty">Not assessed</span>
                    }
                  </div>
                  <div className="id-inv-field">
                    <span className="id-inv-label">State Reason</span>
                    <span className={`id-inv-value${incident.state_reason ? ' id-inv-value--filled' : ' id-inv-value--empty'}`}>
                      {incident.state_reason || 'Not specified'}
                    </span>
                  </div>
                  <div className="id-inv-field">
                    <span className="id-inv-label">Resolution Type</span>
                    <span className={`id-inv-value${incident.resolution_type ? ' id-inv-value--filled' : ' id-inv-value--empty'}`}>
                      {incident.resolution_type || 'Not set'}
                    </span>
                  </div>
                  {incident.resolution_notes && (
                    <div className="id-inv-field id-inv-field--full">
                      <span className="id-inv-label">Resolution Notes</span>
                      <span className="id-inv-value id-inv-value--filled">{incident.resolution_notes}</span>
                    </div>
                  )}
                </div>
              </div>

{/* War Room Panel */}
            {incident.war_room_required && (
              <div className="card id-card id-war-room-panel">
                <div className="id-war-room-header">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                  <h2 className="section-title">War Room Active</h2>
                </div>
                <p className="id-war-room-desc">This incident has been escalated to war room level. All relevant stakeholders should be engaged immediately.</p>
                <div className="id-war-room-details">
                  <div className="id-war-room-detail">
                    <span className="id-inv-label">Incident</span>
                    <span className="id-inv-value">{incident.reference_number}</span>
                  </div>
                  <div className="id-war-room-detail">
                    <span className="id-inv-label">Service</span>
                    <span className="id-inv-value">{incident.service}</span>
                  </div>
                  <div className="id-war-room-detail">
                    <span className="id-inv-label">Severity</span>
                    <span className="id-inv-value">{incident.severity}</span>
                  </div>
                  <div className="id-war-room-detail">
                    <span className="id-inv-label">Client Blast Radius</span>
                    <span className="id-inv-value">{incident.client_blast_radius || 'Not assessed'}</span>
                  </div>
                </div>
              </div>
            )}

              {/* Tabbed card: Activity | Escalations | PIR */}
              <div className="card id-card id-tabbed-card">
                  <div className="id-tabs">
                    <button className={`id-tab${activeTab === 'activity' ? ' id-tab--active' : ''}`} onClick={() => setActiveTab('activity')}>
                      Activity
                      <span className={`id-tab-count${activeTab === 'activity' ? ' id-tab-count--active' : ''}`}>{history.length}</span>
                    </button>
                    <button className={`id-tab${activeTab === 'escalations' ? ' id-tab--active' : ''}`} onClick={() => setActiveTab('escalations')}>
                      Escalations
                      <span className={`id-tab-count${activeTab === 'escalations' ? ' id-tab-count--active' : ''}`}>{escalations.length}</span>
                    </button>
                    <button className={`id-tab${activeTab === 'pir' ? ' id-tab--active' : ''}`} onClick={() => setActiveTab('pir')}>
                      Post-Incident Review
                    </button>
                    <button className={`id-tab${activeTab === 'comments' ? ' id-tab--active' : ''}`} onClick={() => setActiveTab('comments')}>
                      Comments
                      {comments.length > 0 && <span className={`id-tab-count${activeTab === 'comments' ? ' id-tab-count--active' : ''}`}>{comments.length}</span>}
                    </button>
                  </div>

                  {activeTab === 'activity' && (
                    <div>
                      {history.length === 0 ? (
                        <p className="id-no-history">No activity recorded yet.</p>
                      ) : (
                        <div className="id-history">
                          {history.map((h) => (
                            <div key={h.history_id} className="id-history-item">
                              <div className="id-history-dot" />
                              <div className="id-history-content">
                                <div className="id-history-header">
                                  <span className="id-history-user">{h.changed_by}</span>
                                  <span className="id-history-time" title={formatTimestamp(h.created_at)}>{formatRelativeTime(h.created_at)}</span>
                                </div>
                                {h.from_status && h.to_status && (
                                  <div className="id-history-transition">
                                    <span className={`incidents-status incidents-status--${getStatusClass(h.from_status)}`}>{h.from_status.replace(/_/g, ' ')}</span>
                                    <span className="id-history-arrow">{'→'}</span>
                                    <span className={`incidents-status incidents-status--${getStatusClass(h.to_status)}`}>{h.to_status.replace(/_/g, ' ')}</span>
                                  </div>
                                )}
                                {h.note && <p className="id-history-note">{h.note}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'escalations' && (
                    <div>
                      {escalations.length > 0 && (
                        <div className="id-escalation-timeline">
                          {ESCALATION_CHAIN.map((role, index) => {
                            const reached = escalations.some((e) => e.to_role === role || e.from_role === role);
                            // Was escalations[length - 1], which is the OLDEST
                            // entry: the API returns them newest-first, so the
                            // "!" marker sat on the step the incident had long
                            // since climbed past.
                            const current = index === escalationIndex;
                            return (
                              <div key={role} className={`id-timeline-step${reached ? ' id-timeline-step--reached' : ''}${current ? ' id-timeline-step--current' : ''}`}>
                                <div className="id-timeline-dot">
                                  {reached && !current && <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><path d="M20 6L9 17l-5-5"/></svg>}
                                  {current && '!'}
                                </div>
                                {index < 3 && <div className={`id-timeline-line${reached ? ' id-timeline-line--reached' : ''}`} />}
                                <span className="id-timeline-label">{role.replace(/_/g, '\n')}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '14px' }}>
                        {/* Nothing above Executive, so there is nowhere to send
                            it — say so rather than opening a form that cannot
                            do anything. */}
                        <button
                          className="btn btn-secondary id-escalate-btn"
                          onClick={() => { setEscalateError(''); setEscalateToRole(nextEscalationRole || ''); setShowEscalateForm(true); }}
                          disabled={!nextEscalationRole}
                          title={nextEscalationRole
                            ? `Escalates to ${ROLE_LABELS[nextEscalationRole]}, the next level`
                            : 'Already at Executive, the top of the escalation chain'}
                        >
                          Escalate ↑
                        </button>
                      </div>
                      {escalations.length === 0 ? (
                        <p className="id-no-history">No escalations yet.</p>
                      ) : (
                        <div className="id-escalations">
                          {escalations.map((e) => (
                            <div key={e.escalation_id} className="id-escalation-item">
                              <div className="id-escalation-roles">
                                <span className="id-escalation-role">{e.from_role.replace(/_/g, ' ')}</span>
                                <span className="id-history-arrow">{'→'}</span>
                                <span className="id-escalation-role id-escalation-role--to">{e.to_role.replace(/_/g, ' ')}</span>
                              </div>
                              <p className="id-escalation-reason">{e.reason}</p>
                              <div className="id-escalation-meta">
                                <span className={`id-escalation-trigger${e.triggered_by === 'SYSTEM' ? ' id-escalation-trigger--system' : ''}`}>{e.triggered_by}</span>
                                <span className="id-history-time" title={formatTimestamp(e.escalated_at)}>{formatRelativeTime(e.escalated_at)}</span>
                                {e.resolved_at ? <span className="id-escalation-resolved">Resolved</span> : <span className="id-escalation-active">Active</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'pir' && (
                    <div>
                      {['RESOLVED', 'CLOSED'].includes(incident.status) ? (
                        <>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                            <button className="btn btn-secondary id-escalate-btn" onClick={() => { setReviewRcaSummary(review?.rcaSummary || ''); setReviewContributingFactors(review?.contributingFactors || ''); setReviewActionItems(review?.actionItems || ''); setShowReviewForm(true); }}>
                              {review ? 'Edit Review' : 'Submit Review'}
                            </button>
                          </div>
                          {review ? (
                            <div className="id-review-details">
                              <div className="id-inv-field">
                                <span className="id-inv-label">RCA Summary</span>
                                <span className="id-inv-value id-inv-value--filled">{review.rcaSummary}</span>
                              </div>
                              {review.contributingFactors && (
                                <div className="id-inv-field">
                                  <span className="id-inv-label">Contributing Factors</span>
                                  <span className="id-inv-value id-inv-value--filled">{review.contributingFactors}</span>
                                </div>
                              )}
                              {review.actionItems && (
                                <div className="id-inv-field">
                                  <span className="id-inv-label">Action Items</span>
                                  <span className="id-inv-value id-inv-value--filled">{review.actionItems}</span>
                                </div>
                              )}
                              <div className="id-inv-field">
                                <span className="id-inv-label">Reviewed By</span>
                                <span className="id-inv-value">{review.reviewedBy} · {formatRelativeTime(review.reviewedAt)}</span>
                              </div>
                            </div>
                          ) : reviewLoadError ? (
                            // Distinct from "none submitted yet" on purpose: an
                            // unreadable review and an absent one look identical
                            // to the reader otherwise.
                            <div className="alert alert-error">{reviewLoadError}</div>
                          ) : (
                            <p className="id-no-history">No post-incident review submitted yet.</p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="id-no-history" style={{ marginBottom: '16px' }}>PIR is available once the incident is resolved or closed.</p>
                          <button className="btn btn-secondary id-escalate-btn" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>Submit Review</button>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'comments' && (
                    <div>
                      <div className="id-comment-list">
                        {comments.length === 0 ? (
                          <p className="id-no-history">No comments yet.</p>
                        ) : (
                          comments.map((c, i) => (
                            <div key={i} className="id-comment-item">
                              <div className="id-comment-meta">
                                <span className="id-comment-author">{c.author}</span>
                                <span className="id-history-time">{c.time}</span>
                              </div>
                              <p className="id-comment-body">{c.text}</p>
                            </div>
                          ))
                        )}
                      </div>
                      <div className="id-comment-input-row">
                        <textarea
                          className="id-comment-textarea"
                          rows={3}
                          placeholder="Add a comment…"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                        />
                        <button
                          className="id-comment-submit"
                          disabled={!commentText.trim()}
                          onClick={() => {
                            if (!commentText.trim()) return;
                            setComments([...comments, { author: getUserIdentifier() || 'You', time: 'just now', text: commentText.trim() }]);
                            setCommentText('');
                          }}
                        >
                          Post Comment
                        </button>
                      </div>
                    </div>
                  )}
                </div>

            </div>
            {/* end id-main */}

            {/* ── METADATA SIDEBAR ── */}
            <div className="id-sidebar">
              <div className="card id-card">
                <p className="id-sidebar-title">Details</p>

                <div className="id-sidebar-field" ref={statusRef}>
                  <span className="id-sidebar-label">Status</span>
                  <div className="id-inline-edit">
                    <span className={`incidents-status incidents-status--${getStatusClass(incident.status)} id-editable-badge`} onClick={() => setEditingStatus(!editingStatus)} title="Click to change">
                      {incident.status?.replace(/_/g, ' ')}
                    </span>
                    {editingStatus && (
                      <div className="id-dropdown">
                        {STATUS_OPTIONS.map((opt) => {
                          // Shown as unavailable rather than only refused on
                          // click: a disabled option with a reason attached
                          // tells someone what is needed before they commit to
                          // the action, instead of after.
                          const blocker = resolutionBlocker(opt);
                          return (
                            <button
                              key={opt}
                              className={`id-dropdown-item ${opt === incident.status ? 'id-dropdown-item--active' : ''}`}
                              onClick={() => handleStatusSelect(opt)}
                              disabled={Boolean(blocker)}
                              title={blocker || undefined}
                            >
                              <span className={`incidents-status incidents-status--${getStatusClass(opt)}`}>{opt.replace(/_/g, ' ')}</span>
                              {opt === incident.status && <span className="id-check">{'✓'}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">Severity</span>
                  <span className={`incidents-severity incidents-severity--${getSeverityClass(incident.severity)}`}>{incident.severity}</span>
                </div>

                <div className="id-sidebar-field" ref={assigneeRef}>
                  <span className="id-sidebar-label">Assignee</span>
                  <div className="id-inline-edit">
                    <span className="id-assignee-value id-editable" onClick={() => setEditingAssignee(!editingAssignee)} title="Click to change">
                      {incident.assignee || 'Unassigned'}
                      <span className="id-edit-icon">
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </span>
                    </span>
                    {editingAssignee && (
                      <div className="id-dropdown">
                        <button className={`id-dropdown-item ${!incident.assignee ? 'id-dropdown-item--active' : ''}`} onClick={() => handleAssigneeSelect('')}>
                          Unassigned {!incident.assignee && <span className="id-check">{'✓'}</span>}
                        </button>
                        {users.map((u) => (
                          <button key={u.username} className={`id-dropdown-item ${u.username === incident.assignee ? 'id-dropdown-item--active' : ''}`} onClick={() => handleAssigneeSelect(u.username)}>
                            {u.username} {u.username === incident.assignee && <span className="id-check">{'✓'}</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">Assigned By</span>
                  <span className="id-sidebar-value">{incident.assigner || '—'}</span>
                </div>

                <hr className="id-sb-divider" />

                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">War Room</span>
                  <span className={`id-war-room-badge ${incident.war_room_required ? 'id-war-room--yes' : ''}`}>
                    {incident.war_room_required ? '⚠ Required' : 'Not Required'}
                  </span>
                </div>

              {/* SLA */}
              <div className="id-sidebar-field">
                <span className="id-sidebar-label">SLA Deadline</span>
                <span className="id-sidebar-value">{formatTimestamp(incident.sla_deadline)}</span>
              </div>

              <div className="id-sidebar-field">
                <span className="id-sidebar-label">Resolution Deadline</span>
                <span className="id-sidebar-value">{formatTimestamp(incident.resolution_deadline)}</span>
              </div>

              <div className="id-sidebar-field">
                <span className="id-sidebar-label">SLA Clock</span>
                <span className={`id-sla-clock-badge ${incident.sla_clock_paused ? 'id-sla-clock--paused' : 'id-sla-clock--running'}`}>
                  {incident.sla_clock_paused ? 'Paused' : 'Running'}
                </span>
                {incident.total_paused_minutes > 0 && (
                  <span className="id-sidebar-value" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                    {incident.total_paused_minutes}m paused total
                  </span>
                )}
              </div>

              <div className="id-sidebar-field">
                <span className="id-sidebar-label">First Response</span>
                {incident.first_response_met == null ? (
                  <span className="id-sidebar-value">—</span>
                ) : incident.first_response_met ? (
                  <span className="id-sla-clock-badge id-sla-clock--met">Met</span>
                ) : (
                  <span className="id-sla-clock-badge id-sla-clock--missed">Missed</span>
                )}
              </div>

                <hr className="id-sb-divider" />

                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">Created</span>
                  <span className="id-sidebar-value">{formatTimestamp(incident.created_at)}</span>
                </div>
                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">Last Updated</span>
                  <span className="id-sidebar-value">{formatTimestamp(incident.updated_at)}</span>
                </div>
                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">Resolved</span>
                  <span className="id-sidebar-value">{formatTimestamp(incident.resolved_at)}</span>
                </div>

                <hr className="id-sb-divider" />

                <div className="id-sidebar-field">
                  <span className="id-sidebar-label">Post-Incident Review</span>
                  <span className={`id-pir-badge ${review ? 'id-pir-badge--complete' : 'id-pir-badge--pending'}`}>
                    {review ? 'PIR Complete' : 'PIR Pending'}
                  </span>
                </div>

              </div>

            </div>

          </div>
          {/* end id-layout */}

        </div>
      )}

      {/* Status transition note dialog */}
      {showStatusNote && (
        <div className="confirm-overlay" onClick={handleStatusCancel}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Change Status</h3>
            <p className="confirm-message">
              Transition to <strong>{pendingStatus.replace(/_/g, ' ')}</strong>
            </p>
            <textarea
              className="id-status-note-input"
              placeholder="Add a note (optional)..."
              value={statusNote}
              onChange={(e) => setStatusNote(e.target.value)}
              rows={3}
              autoFocus
            />
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={handleStatusCancel}>Cancel</button>
              <button className="btn btn-primary" onClick={handleStatusConfirm} disabled={saving}>
                {saving ? 'Saving...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Escalate form dialog */}
      {showEscalateForm && (
        <div className="confirm-overlay" onClick={() => setShowEscalateForm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Escalate Incident</h3>
            {escalateError && <div className="alert alert-error">{escalateError}</div>}
            <div className="id-escalate-form">
              <label className="id-form-label">Escalate To</label>
              {/*
                Every role is listed so the ladder is visible, but only the next
                one can be chosen. This used to be a free choice that the API
                then ignored — pick First Responder on an incident sitting at
                Delivery Manager and it escalated to Executive anyway, telling
                the user nothing. A form must not ask a question it cannot
                honour, and the rule belongs where the choice is made rather
                than in a complaint afterwards.
              */}
              <select
                className="id-form-select"
                value={escalateToRole}
                onChange={(e) => setEscalateToRole(e.target.value)}
              >
                {ESCALATION_CHAIN.map((role) => (
                  <option key={role} value={role} disabled={role !== nextEscalationRole}>
                    {ROLE_LABELS[role]}
                    {role === nextEscalationRole ? '' : ' — not the next level'}
                  </option>
                ))}
              </select>
              <p className="id-form-hint">
                Escalation follows the chain one level at a time, so this incident
                can only go to <strong>{ROLE_LABELS[nextEscalationRole] || '—'}</strong>.
              </p>
              <label className="id-form-label">Reason <span className="id-form-required">*</span></label>
              <textarea
                className="id-status-note-input"
                placeholder="Reason for escalation..."
                value={escalateReason}
                onChange={(e) => setEscalateReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => { setShowEscalateForm(false); setEscalateToRole(''); setEscalateReason(''); setEscalateError(''); }}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleEscalate}
                disabled={escalating || !escalateToRole || !escalateReason.trim()}
              >
                {escalating ? 'Escalating...' : 'Escalate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-Incident Review form dialog */}
      {showReviewForm && (
        <div className="confirm-overlay" onClick={() => setShowReviewForm(false)}>
          <div className="confirm-dialog confirm-dialog--wide" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Post-Incident Review</h3>
            {reviewError && <div className="alert alert-error">{reviewError}</div>}
            <div className="id-escalate-form">
              <label className="id-form-label">RCA Summary <span className="id-form-required">*</span></label>
              <textarea
                className="id-status-note-input"
                placeholder="What was the root cause of this incident?"
                value={reviewRcaSummary}
                onChange={(e) => setReviewRcaSummary(e.target.value)}
                rows={3}
                autoFocus
              />
              <label className="id-form-label">Contributing Factors</label>
              <textarea
                className="id-status-note-input"
                placeholder="What factors contributed to this incident?"
                value={reviewContributingFactors}
                onChange={(e) => setReviewContributingFactors(e.target.value)}
                rows={3}
              />
              <label className="id-form-label">Action Items</label>
              <textarea
                className="id-status-note-input"
                placeholder="What actions will prevent recurrence?"
                value={reviewActionItems}
                onChange={(e) => setReviewActionItems(e.target.value)}
                rows={3}
              />
            </div>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => { setShowReviewForm(false); setReviewError(''); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveReview} disabled={savingReview || !reviewRcaSummary.trim()}>
                {savingReview ? 'Saving...' : 'Submit Review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resolution form dialog */}
      {showResolutionForm && (
        <div className="confirm-overlay" onClick={() => setShowResolutionForm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Resolution Details</h3>
            {resolutionError && <div className="alert alert-error">{resolutionError}</div>}
            <div className="id-escalate-form">
              <label className="id-form-label">Resolution Type</label>
              <select
                className="id-form-select"
                value={resolutionType}
                onChange={(e) => setResolutionType(e.target.value)}
              >
                <option value="">Select type...</option>
                <option value="CLEAN_UP">Clean Up</option>
                <option value="CONFIG_CHANGE">Config Change</option>
                <option value="REPLAY">Replay</option>
                <option value="PATCH">Patch</option>
                <option value="ROLLBACK">Rollback</option>
                <option value="OTHER">Other</option>
              </select>
              <label className="id-form-label">Resolution Notes</label>
              <textarea
                className="id-status-note-input"
                placeholder="Describe what was done to resolve the incident..."
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
              />
            </div>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => setShowResolutionForm(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                onClick={handleSaveResolution}
                disabled={savingResolution || !hasResolutionToSave}
              >
                {savingResolution ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel incident confirm */}
      {showCancelConfirm && (
        <div className="confirm-overlay" onClick={() => setShowCancelConfirm(false)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">Cancel Incident?</h3>
            <p className="confirm-message">
              Are you sure you want to cancel this incident? This cannot be undone.
            </p>
            <div className="confirm-actions">
              <button className="btn btn-secondary" onClick={() => setShowCancelConfirm(false)}>Go Back</button>
              <button className="btn id-cancel-btn" onClick={handleCancelIncident} disabled={deleting}>
                {deleting ? 'Cancelling...' : 'Cancel Incident'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IncidentDetailPage;
